import type { ReactElement } from "react";
import { useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import type { IFlattenedCategory } from "app/common/services/categoryService.types";
import { SelectCombobox } from "app/pages/sales-intelligence/common-components/dropdown/SelectCombobox";
import { renderHighlightedOption } from "app/pages/sales-intelligence/common-components/dropdown/utils/renderHighlightedOption";
import { FilterSuggestionsBar } from "app/pages/sales-intelligence/common-components/FilterSuggestionsBar";
import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import useIndustrySearch from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useIndustrySearch";
import useIndustrySuggestions from "app/pages/sales-intelligence/modules/leads-search-filters/hooks/useIndustrySuggestions";
import type LegacyIndustryFilterModel from "app/pages/sales-intelligence/modules/leads-search-filters/models/LegacyIndustryFilter/LegacyIndustryFilterModel";
import leadsSearchFiltersI18nKeys from "app/pages/sales-intelligence/modules/leads-search-filters/utils/i18n";
import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";

import { FilterContentContainer, StyledHorizontalRadioSelect } from "../styles";

import { StyledDDContainer } from "./styles";

const DROPDOWN_ID = "si-website-industry-dropdown";
const DROPDOWN_WRAPPER_CLASSNAME = "si-website-industry-dropdown-wrapper";

/** @deprecated Flat 2-level industry view. Superseded by the taxonomy tree filter (SI-8780). */
const LegacyIndustryFilter = ({
    value,
    filter,
    onFilterUpdate,
}: IFilterViewBaseProps<LegacyIndustryFilterModel>): ReactElement | null => {
    const { translate } = useTranslate();

    const { types, industries, categoriesById, suggestedIndustries } = filter;

    const [inclusionType, setInclusionType] = useState<InclusionOption>(value.inclusion);
    const [ignoredIndustries, setIgnoredIndustries] = useState<string[]>([]);

    const { suggestedIndustryChips, selectStrategy, isLoading } = useIndustrySuggestions({
        value,
        industries,
        categoriesById,
        ignoredIndustries,
        suggestedIndustries,
        onFilterUpdate,
        onIgnoreIndustries: setIgnoredIndustries,
        inclusion: inclusionType,
    });

    const { handleSearchChange, searchTerm } = useIndustrySearch(inclusionType);

    const selectedIndustryIds = selectStrategy.getSelectedItemIds();
    const wasIndustrySelected = selectedIndustryIds.length > 0;
    const selectedIndustryIdSet = new Set(selectedIndustryIds);

    const handleTypeChange = (type: InclusionOption) => {
        setInclusionType(type);
        if (value?.selectedIndustries?.length) {
            onFilterUpdate({ ...value, inclusion: type });
        }
    };

    return (
        <FilterContentContainer>
            <StyledHorizontalRadioSelect
                items={types.map((name: string) => ({
                    id: name,
                    text: translate(`si.common.inclusions.${name}`),
                }))}
                size="sm"
                selected={inclusionType}
                onSelect={handleTypeChange}
                className={DROPDOWN_WRAPPER_CLASSNAME}
            />
            <FilterSuggestionsBar
                suggestedItems={suggestedIndustryChips}
                isEnabled={wasIndustrySelected}
                isLoading={isLoading}
            />

            <StyledDDContainer id={DROPDOWN_ID}>
                <SelectCombobox<IFlattenedCategory>
                    items={selectStrategy.getItems(searchTerm)}
                    isItemSelected={(item) => selectedIndustryIdSet.has(item.id)}
                    onSelect={selectStrategy.onSelect}
                    buttonText={translate(`${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.dd_button_text`)}
                    hasSearch
                    searchPlaceHolder={translate(
                        `${leadsSearchFiltersI18nKeys.filterBase("categoryFilter")}.dd_placeholder`,
                    )}
                    searchValue={searchTerm}
                    onSearchChange={handleSearchChange}
                    // Items are pre-filtered by selectStrategy.getItems(searchTerm); don't double-filter.
                    filterItem={() => true}
                    renderOption={(item, isSelected) => renderHighlightedOption(item, isSelected, searchTerm)}
                />
            </StyledDDContainer>
        </FilterContentContainer>
    );
};

export default LegacyIndustryFilter;
